import React, { useState, useEffect, useRef, FC } from 'react';
import './leadsolveChat.css';
import { usePortal } from '../../../context/PortalContext';
import { useUserAuth } from '../../../context/AuthContext';

import typing_gif from '../../../assets/images/typing_gif.gif';


// 1. Define your Interfaces
interface Message {
    text: string;
    sender: 'user' | 'bot';
    id: number;
}

interface LeadSolveChatProps {
    widgetContext: string;
}

const LeadSolveChatWidget: FC<LeadSolveChatProps> = (props) => {


    const { clientData } = usePortal();
    const { user, loading: authLoading } = useUserAuth();

    const showDelay = 0;
    const showMessageDelay = 2000;
    const closeMessageDelay = 10000;

    const EMBED_WHITELIST = ["calendly.com", "pense.co.uk", "portal.pense.co.uk"];
    const webhookURL = "https://n8n.leadsolve.cloud/webhook/da581a0e-49a3-4237-a39e-70ace1e8309f";


    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [messages, setMessages] = useState<Message[]>(() => {
        // Load messages from sessionStorage on initialization
        const stored = sessionStorage.getItem("leadsolveWidget/messages");
        return stored ? JSON.parse(stored) : [];
    });
    const [inputValue, setInputValue] = useState<string>('');
    const [isTyping, setIsTyping] = useState<number>(0);
    const [modalUrl, setModalUrl] = useState<string | null>(null);
    const [show, setShow] = useState<boolean>(false);
    const [isConnecting, setIsConnecting] = useState<boolean>(false);

    sessionStorage.setItem("leadsolveWidgetShown", "true");
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const popupMessageRef = useRef<HTMLDivElement>(null);
    const sessionId = useRef(sessionStorage.getItem("leadsolveWidget/sessionId") || crypto.randomUUID());


    let widgetTimer: NodeJS.Timeout | undefined;
    let messageTimer: NodeJS.Timeout | undefined;
    let messageCloseTimer: NodeJS.Timeout | undefined;

    useEffect(() => {
        if (!!sessionStorage.getItem("leadsolveWidgetShown")) {
            setShow(true);
            return;
        }

        if (authLoading || !user) return; // Wait for auth state to load
        setShow(!!user);
    }, [user, authLoading]);

    // Auto-init logic
    useEffect(() => {
        sessionStorage.setItem("leadsolveWidget/sessionId", sessionId.current);

        const shouldAutoOpen = sessionStorage.getItem("leadsolveWidget/chatOpened");
        const messageShouldShow = !sessionStorage.getItem("leadsolveWidget/MessageClosed");


        if (shouldAutoOpen && show) {
            widgetTimer = setTimeout(() => {
                toggleChat(true);
                if (!sessionStorage.getItem("leadsolveWidget/initFired")) {
                    triggerInit();
                }
            }, showDelay);
        }

        if (messageShouldShow) {
            messageTimer = setTimeout(() => {
                showMessagePopup();
            }, showMessageDelay);
        }

        if (messageShouldShow) {
            messageCloseTimer = setTimeout(() => {
                closeMessage(false);
            }, closeMessageDelay + showMessageDelay)
        }

        return () => {
            if (widgetTimer)
                clearTimeout(widgetTimer);

            if (messageTimer)
                clearTimeout(messageTimer);

            if (messageCloseTimer)
                clearTimeout(messageCloseTimer);
        }
    }, [show]);

    // Scroll to bottom
    useEffect(() => {
        sessionStorage.setItem("leadsolveWidget/messages", JSON.stringify(messages));

        const timer = setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 200);
        return () => clearTimeout(timer);
    }, [messages]);

    const toggleChat = (forceState?: boolean) => {
        const newState = forceState !== undefined ? forceState : !isOpen;
        setIsOpen(newState);

        // PERSISTENCE: Save the user's manual toggle choice
        if (newState) {
            sessionStorage.setItem("leadsolveWidget/chatOpened", "true");
            sessionStorage.removeItem("leadsolveWidget/chatClosed");
            if (!sessionStorage.getItem("leadsolveWidget/initFired"))
                triggerInit();

            if (popupMessageRef?.current && !sessionStorage.getItem("leadsolveWidget/MessageClosed"))
                closeMessage();

        } else {
            sessionStorage.setItem("leadsolveWidget/chatClosed", "true");
            sessionStorage.removeItem("leadsolveWidget/chatOpened");
        }
    };

    const triggerInit = async () => {
        sessionStorage.setItem("leadsolveWidget/initFired", "true");
        try {
            setIsConnecting(true);
            const data = await sendMessageToWebhook("init", true);
            if (data.reply) addMessage(data.reply, 'bot');
        } catch (e) {
            addMessage("Welcome to Pense. How can I help today?", "bot");
        } finally {
            setIsConnecting(false);
        }
    };

    const addMessage = (text: string, sender: 'user' | 'bot') => {
        setMessages(prev => [...prev, { text, sender, id: Date.now() }]);
    };

    const showMessagePopup = () => {
        if (popupMessageRef)
            popupMessageRef.current?.classList.add("active");
    }

    const closeMessage = (isInstant = true) => {

        if (messageTimer)
            clearTimeout(messageTimer);

        if (popupMessageRef?.current) {
            if (isInstant)
                popupMessageRef.current.style.transition = "none";

            popupMessageRef.current.classList.remove("active");
        }

        sessionStorage.setItem("leadsolveWidget/MessageClosed", "true");
    }

    const handleSend = async () => {
        const userMsg = inputValue.trim();
        if (!userMsg) return;

        setInputValue('');
        addMessage(userMsg, 'user');
        try {
            const data = await sendMessageToWebhook(userMsg, false);
            addMessage(data.reply || "Sorry, no reply received.", "bot");
        } catch (e) {
            addMessage("Sorry, there was a problem.", "bot");
        }
    };

    const sendMessageToWebhook = async (message: string, isReset: boolean = false) => {
        const payload = {
            message: message,
            reset: isReset,
            sessionId: sessionId.current,
            context: props.widgetContext,
            clientId: (clientData?.email ?? "Unknown") + ` (${"CLI-" + (clientData?.id?.padStart(8, '0') ?? "No ID")})`,
            locationId: "Wpk8f3sik4u77IaiP5yI"
        };

        setIsTyping(prev => prev + 1);

        const response = await fetch(webhookURL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
        });

        setIsTyping(prev => prev - 1);

        return response.json();
    };

    const handleKeyboardSend = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return;
        handleSend();
    };

    const handleLinkClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        const anchor = target.closest('a');
        if (!anchor) return;

        const href = anchor.href;
        e.preventDefault();

        const isWhitelisted = EMBED_WHITELIST.some(d => {
            try { return new URL(href).hostname.endsWith(d); } catch { return false; }
        });

        if (isWhitelisted) {
            setModalUrl(href);
        } else {
            window.open(href, "_blank");
        }
    };

    if (!show) return null;


    return (
        <>
            <div ref={popupMessageRef} className="message-popup-container" >
                <div className="message-popup-text" onClick={() => { closeMessage(); toggleChat(true) }}>Hi, we're here to help, just let us know if you have any questions.</div>
                <div className="message-popup-close" onClick={() => closeMessage()}><i className="fas fa-times"></i></div>
            </div>
            <button className={`pense-chat-button ${isOpen ? 'active' : ''}`} onClick={() => toggleChat()}>
                <i className="fas fa-comment"></i>
            </button>
            <div className={`pense-chat-container ${isOpen ? 'active' : ''}`}>
                <div className="pense-chat-header" onClick={() => toggleChat()}>
                    <div>Chat With Us</div>
                    <div><i style={{ color: 'white', fontSize: '1.5rem' }} className="fa-solid fa-xmark"></i></div>
                </div>
                <div className="pense-chat-body" onClick={handleLinkClick}>
                    {messages.map(m => (
                        <div
                            key={m.id}
                            className={`chat-bubble ${m.sender}`}
                            dangerouslySetInnerHTML={m.sender === 'bot' ? { __html: linkify(m.text) } : undefined}
                        >
                            {m.sender === 'user' ? m.text : null}
                        </div>
                    ))}
                    {isTyping > 0 && (
                        <div className="chat-bubble bot typing-container">
                            {isConnecting ? "Connecting " : ""}
                            <img
                                src={typing_gif}
                                alt="Typing..."
                                style={{ width: '40px', display: 'block' }}
                            />
                        </div>
                    )}
                    <div
                        ref={messagesEndRef}
                        style={{ clear: 'both', height: '1px', width: '100%' }}
                    />
                </div>
                <div className="pense-chat-input">
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyboardSend}
                        placeholder="Type your message..."
                    />
                    <div className="pense-send-button" onClick={handleSend}>
                        <i style={{ color: 'white', fontSize: '1.25rem' }} className="fas fa-paper-plane"></i>
                    </div>
                </div>
            </div>

            {/* Modal Overlay */}
            {modalUrl && (
                <div className="pense-modal-backdrop" style={{ display: 'flex' }}>
                    <div className="pense-modal">
                        <div className="pense-modal-toolbar">
                            <button onClick={() => setModalUrl(null)}>Close</button>
                            <div className="pense-modal-url">{modalUrl}</div>
                            <button onClick={() => window.open(modalUrl, "_blank")}>Open</button>
                        </div>
                        <iframe src={modalUrl} title="External Content" className="pense-modal-iframe" />
                    </div>
                </div>
            )}
        </>
    );
};

function linkify(text: string): string {
    // 1. Match the protocol and main body
    // 2. Stop before any trailing punctuation (.,!?) followed by a boundary
    const urlRe = /(https?:\/\/[^\s)]+?)([.?,!]?(?=\s|\)|$))/g;

    return text.replace(urlRe, (match, url, punctuation) => {
        const esc = url.replace(/"/g, "&quot;");
        const link = `<a href="${esc}" target="_self" rel="nofollow noopener">${esc}</a>`;

        // Return the link + the punctuation (which remains outside the tag)
        return link + punctuation;
    });
}

export default LeadSolveChatWidget;
